import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Gavel, Save, Trash2, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { LEILOEIROS } from "@/lib/simulator";

interface AuctioneerRule {
  id: string;
  leiloeiro_id: string;
  rules_text: string;
  pdf_url?: string;
  updated_at: string;
}

export default function AdminLeiloeiros() {
  const [rules, setRules] = useState<AuctioneerRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingPdfUrl, setEditingPdfUrl] = useState("");
  const [selectedLeiloeiro, setSelectedLeiloeiro] = useState<string>(LEILOEIROS[0].id);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  async function fetchRules() {
    setLoading(true);
    const { data, error } = await supabase
      .from("auctioneer_rules")
      .select("*")
      .order("updated_at", { ascending: false });

    if (error) {
      console.error(error);
      toast.error("Erro ao carregar regras");
    } else {
      setRules(data || []);
    }
    setLoading(false);
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Por favor, selecione um arquivo PDF");
      return;
    }

    try {
      setUploadingPdf(true);
      const fileExt = file.name.split(".").pop();
      const fileName = `${selectedLeiloeiro}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { data, error: uploadError } = await supabase.storage
        .from("rules")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("rules")
        .getPublicUrl(filePath);

      setEditingPdfUrl(publicUrl);
      toast.success("PDF enviado com sucesso");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar PDF");
    } finally {
      setUploadingPdf(false);
    }
  }

  async function handleSave() {
    if (!editingText.trim() && !editingPdfUrl) {
      return toast.warning("Forneça o texto da regra ou um PDF de edital");
    }

    const payload = {
      leiloeiro_id: selectedLeiloeiro,
      rules_text: editingText,
      pdf_url: editingPdfUrl,
      updated_at: new Date().toISOString(),
    };

    let error;
    if (editingId) {
      ({ error } = await supabase
        .from("auctioneer_rules")
        .update(payload)
        .eq("id", editingId));
    } else {
      ({ error } = await supabase
        .from("auctioneer_rules")
        .insert([payload]));
    }

    if (error) {
      console.error(error);
      toast.error("Erro ao salvar regra");
    } else {
      toast.success("Regra salva com sucesso");
      setEditingId(null);
      setEditingText("");
      setEditingPdfUrl("");
      fetchRules();
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Deseja realmente excluir esta regra?")) return;

    const { error } = await supabase
      .from("auctioneer_rules")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Erro ao excluir");
    } else {
      toast.success("Regra excluída");
      fetchRules();
    }
  }

  const startEdit = (rule: AuctioneerRule) => {
    setEditingId(rule.id);
    setEditingText(rule.rules_text || "");
    setEditingPdfUrl(rule.pdf_url || "");
    setSelectedLeiloeiro(rule.leiloeiro_id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b-4 border-foreground pb-4">
        <div className="flex items-center gap-3">
          <Gavel className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-heading font-black uppercase tracking-tighter">Regras de Leiloeiros</h1>
        </div>
        {!editingId && (
          <Button onClick={() => { setEditingId(null); setEditingText(""); setEditingPdfUrl(""); }} className="font-bold border-2 border-foreground shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
            <Plus className="w-4 h-4 mr-2" /> Nova Regra
          </Button>
        )}
      </div>

      {(editingId || !rules.some(r => r.leiloeiro_id === selectedLeiloeiro)) && (
        <Card className="border-4 border-foreground shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] bg-background">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold uppercase tracking-tight">
              {editingId ? "Editar Regra" : "Nova Regra"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Leiloeiro</label>
                <select
                  className="w-full p-2 border-2 border-foreground bg-background font-bold focus:ring-0 focus:border-primary outline-none"
                  value={selectedLeiloeiro}
                  onChange={(e) => setSelectedLeiloeiro(e.target.value)}
                  disabled={!!editingId}
                >
                  {LEILOEIROS.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Upload de Edital (PDF)</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="border-2 border-foreground focus-visible:ring-0 cursor-pointer"
                    disabled={uploadingPdf}
                  />
                  {editingPdfUrl && (
                    <a href={editingPdfUrl} target="_blank" rel="noopener noreferrer" className="p-2 border-2 border-foreground bg-accent hover:bg-accent/80 transition-colors" title="Ver PDF atual">
                      <FileText className="w-5 h-5" />
                    </a>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground uppercase font-mono">
                  {uploadingPdf ? "Enviando arquivo..." : editingPdfUrl ? "Arquivo vinculado" : "Nenhum PDF selecionado"}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black uppercase tracking-widest text-muted-foreground">Texto Extraído / Observações do Edital</label>
              <Textarea
                placeholder="Cole aqui as regras específicas, taxas e condições deste leiloeiro..."
                className="min-h-[200px] border-2 border-foreground focus-visible:ring-0 focus-visible:border-primary font-mono text-sm leading-relaxed"
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => { setEditingId(null); setEditingText(""); setEditingPdfUrl(""); }} className="font-bold">Cancelar</Button>
              <Button onClick={handleSave} className="font-bold border-2 border-foreground shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" disabled={uploadingPdf}>
                <Save className="w-4 h-4 mr-2" /> Salvar Regras
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <p className="col-span-2 text-center py-10 font-mono uppercase text-muted-foreground">Carregando...</p>
        ) : rules.length === 0 ? (
          <p className="col-span-2 text-center py-10 font-mono uppercase text-muted-foreground bg-muted/5 border-2 border-dashed border-muted">Nenhuma regra cadastrada</p>
        ) : (
          rules.map((rule) => {
            const leiloeiro = LEILOEIROS.find(l => l.id === rule.leiloeiro_id);
            return (
              <Card key={rule.id} className="border-4 border-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-background hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg font-heading font-black uppercase tracking-tight">
                      {leiloeiro?.name || rule.leiloeiro_id}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(rule)} title="Editar">
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(rule.id)} className="text-destructive hover:text-destructive" title="Excluir">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground font-mono mb-3 uppercase">Última atualização: {new Date(rule.updated_at).toLocaleString()}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {rule.pdf_url && (
                      <a href={rule.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-black uppercase px-2 py-1 bg-accent border-2 border-foreground shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all">
                        <FileText className="w-3 h-3" /> Ver Edital PDF
                      </a>
                    )}
                  </div>
                  <p className="text-sm line-clamp-3 text-muted-foreground leading-relaxed italic">
                    {rule.rules_text ? `"${rule.rules_text.slice(0, 200)}..."` : "Sem descrição textual disponível."}
                  </p>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
